import { FormViewProps } from "@/components/form/FormView"
import { StaticStore } from "@/lib/store-factory"
import { useNavigate, useParams } from "react-router"
import { store, StoreName } from "@/stores/stores"
import { useSnapshot } from "valtio"
import { FC, useEffect } from "react"
import { clone } from "@/lib/utils"
import { toast } from "sonner"
import { Entity } from "@/lib/schemas"

export interface RouteCreateEditStaticProps<T extends Entity> extends Partial<FormViewProps<T>> {
  storeName: StoreName
  // onEditingItemFound?: (item?: T) => void
  entityLabel?: string
  routePath?: string
  CreateComponent?: FC<FormViewProps<any>>
  EditComponent?: FC<FormViewProps<any>>
}

export function RouteCreateEditStatic<T extends Entity>(props: RouteCreateEditStaticProps<T>) {
  const { CreateComponent, EditComponent, storeName, entityLabel, routePath, ...rest } = props
  const params = useParams()
  const navigate = useNavigate()
  const titleSuffix = params.id === "create" ? "Create" : "Edit"

  const onClose = () => {
    // Remove o último segmento da URL
    const pathSegments = location.pathname.split("/")
    pathSegments.pop() // Remove o ':id'
    const newPath = pathSegments.join("/")

    navigate(routePath ?? newPath)
  }

  const componentProps = (args: { successMessage: string }) => {
    return {
      title: props.title ?? `${titleSuffix} ${entityLabel?.toLocaleLowerCase()}`,
      onSubmitted: () => {
        toast.success(args.successMessage)
        props.onClose?.()
      },
      onClose,
      ...rest,
    }
  }

  return (
    <>
      {!!CreateComponent && (
        <CreateComponent opened={params.id === "create"} {...componentProps({ successMessage: "Successfully created." })} />
      )}
      {!!EditComponent && (
        <RouteEditComponent
          {...props}
          opened={!!params.id && (params.id !== "create" || !CreateComponent)}
          {...componentProps({ successMessage: "Successfully updated." })}
        />
      )}
    </>
  )
}

// ███████╗██████╗ ██╗████████╗
// ██╔════╝██╔══██╗██║╚══██╔══╝
// █████╗  ██║  ██║██║   ██║
// ██╔══╝  ██║  ██║██║   ██║
// ███████╗██████╔╝██║   ██║
// ╚══════╝╚═════╝ ╚═╝   ╚═╝
//
function RouteEditComponent<T extends Entity>(props: RouteCreateEditStaticProps<T>) {
  const { CreateComponent, EditComponent, storeName, entityLabel, routePath, ...rest } = props

  const snap = useSnapshot(store)
  const params = useParams()
  const entityStore = snap[storeName] as StaticStore<any>
  const list = entityStore.list
  const navigate = useNavigate()

  const editingItem = list.find((item) => item.id === params.id)

  useEffect(() => {
    if (!!params.id && params.id !== "create" && entityStore.fetched && !editingItem) {
      navigate(`${routePath}`, {
        replace: true,
      })
    }
  }, [editingItem, params.id, entityStore.fetched, navigate, routePath])

  if (!EditComponent) {
    return <></>
  }

  return <EditComponent {...rest} opened={props.opened && !!editingItem} initialValues={clone(editingItem)} />
}
